import { log } from ".";

// Tipos de cambio fijos (ajustables según necesidades)
const tiposDeCambio = {
  Perú: 3.75,
  Chile: 950,
  Uruguay: 40,
};

// Fecha de corte para ID 32
const EXECUTIVE_CUTOFF_DATE = new Date("2025-06-01");

export const calculateComission = {
  calculateAgencyCommission: (opportunity, clientes, intermediarios) => {
    let comisionIntermediarioPorcentaje = 0;
    if (!opportunity.isBTL) {
      const clienteAsociado = clientes[opportunity.client_id]; // Acceso como objeto
      if (clienteAsociado && clienteAsociado.agencia) {
        const intermediarioAsociado = intermediarios[clienteAsociado.agencia]; // Acceso como objeto
        if (intermediarioAsociado) {
          const fechaFactura = new Date(opportunity.facturafecha || opportunity.created_at || new Date());
          comisionIntermediarioPorcentaje =
            intermediarioAsociado.comision_2 &&
            intermediarioAsociado.comision_2_fechainicio &&
            intermediarioAsociado.comision_2_fechafin &&
            fechaFactura >= new Date(intermediarioAsociado.comision_2_fechainicio) &&
            fechaFactura <= new Date(intermediarioAsociado.comision_2_fechafin)
              ? intermediarioAsociado.comision_2 || 0
              : intermediarioAsociado.comision || 0;
          if ([16, 32, 60].includes(clienteAsociado.agencia)) comisionIntermediarioPorcentaje = 0;
        }
      }
    } else {
      comisionIntermediarioPorcentaje = opportunity.comision || 0;
      if ([16, 32, 60].includes(opportunity.intermediarioId || opportunity.intermediario))
        comisionIntermediarioPorcentaje = 0;
    }
    const importeBruto = opportunity.has_manual_discount
      ? parseFloat(opportunity.original_amount) || 0
      : parseFloat(opportunity.importe) || 0;
    const descuento = opportunity.has_manual_discount ? parseFloat(opportunity.manual_discount_amount) || 0 : 0;
    const neto = importeBruto - descuento;
    const comision = neto * (comisionIntermediarioPorcentaje / 100);
    return opportunity.moneda_dolar ? comision * (tiposDeCambio[opportunity.pais] || 1) : comision;
  },

  calculateBuildingCommission: (opportunity, edificios) => {
    if (!opportunity.edificiosparticipantes || typeof opportunity.edificiosparticipantes !== "string") {
      log.warn(`Invalid edificiosparticipantes for opportunity ${opportunity.id}`, opportunity);
      return 0;
    }
    const edificiosParticipantes = [...new Set(opportunity.edificiosparticipantes.split(",").map(Number))];
    let totalComision = 0;
    const importeTotal = parseFloat(opportunity.importe) || 0;
    const clienteAsociado = opportunity.clientes?.find((cliente) => cliente.id === opportunity.client_id);
    const comisionIntermediarioPorcentaje = clienteAsociado?.agencia
      ? intermediarios.find((i) => i.id === clienteAsociado.agencia)?.comision || 0
      : opportunity.comision || 0;
    const comisionIntermediario = importeTotal * (comisionIntermediarioPorcentaje / 100);
    const netoParaEdificios = importeTotal - comisionIntermediario;
    const fechaFactura = new Date(
      opportunity.facturafecha ||
        (opportunity.mes && opportunity.ano
          ? new Date(parseInt(opportunity.ano, 10), parseInt(opportunity.mes, 10) - 1, 1)
          : opportunity.created_at || new Date())
    );
    const edificioValues = Object.values(edificios); // Convertir objeto a array de valores
    const totalPantallas =
      edificioValues.reduce(
        (sum, ed) =>
          edificiosParticipantes.includes(ed.id)
            ? sum +
              (Number(ed.pantallas || 0) + Number(ed.totems || 0) + Number(ed.espera || 0) + Number(ed.cowork || 0))
            : sum,
        0
      ) || 1; // Fallback a 1 para evitar división por 0
    edificiosParticipantes.forEach((edificioId) => {
      const edificio = edificios[edificioId]; // Acceso directo por id desde el objeto
      if (edificio) {
        let porcentajeComision = 0;
        const periods = [
          {
            inicio: edificio.comision_1_fechainicio,
            fin: edificio.comision_1_fechafin,
            porcentaje: edificio.comision_1_porcentaje,
          },
          {
            inicio: edificio.comision_2_fechainicio,
            fin: edificio.comision_2_fechafin,
            porcentaje: edificio.comision_2_porcentaje,
          },
          {
            inicio: edificio.comision_3_fechainicio,
            fin: edificio.comision_3_fechafin,
            porcentaje: edificio.comision_3_porcentaje,
          },
        ].filter((p) => p.inicio && p.fin);
        for (const period of periods) {
          if (fechaFactura >= new Date(period.inicio) && fechaFactura <= new Date(period.fin)) {
            porcentajeComision = period.porcentaje || 0;
            break;
          }
        }
        const pantallasEdificio =
          Number(edificio.pantallas || 0) +
            Number(edificio.totems || 0) +
            Number(edificio.espera || 0) +
            Number(edificio.cowork || 0) || 1;
        const proporcion = totalPantallas > 0 ? pantallasEdificio / totalPantallas : 1 / edificiosParticipantes.length;
        const comisionPorEdificio = netoParaEdificios * (porcentajeComision / 100) * proporcion;
        totalComision += comisionPorEdificio;
      }
    });
    return !isNaN(totalComision) ? totalComision : 0;
  },

  calculateSalesCommission: (opportunity, clientes, intermediarios, edificios) => {
    const agencyCommission = calculateComission.calculateAgencyCommission(opportunity, clientes, intermediarios);
    const buildingCommission = calculateComission.calculateBuildingCommission(opportunity, edificios);
    const importeBruto = opportunity.has_manual_discount
      ? parseFloat(opportunity.original_amount) || 0
      : parseFloat(opportunity.importe) || 0;
    const descuento = opportunity.has_manual_discount ? parseFloat(opportunity.manual_discount_amount) || 0 : 0;
    const netoInicial = importeBruto - descuento;
    const netoFinal = netoInicial - agencyCommission - buildingCommission;
    const fechaFactura = new Date(opportunity.facturafecha || opportunity.created_at || new Date());
    const month = fechaFactura.getMonth();
    const year = fechaFactura.getFullYear();
    const gerenteIds = [1, 4, 10]; // Definir aquí para consistencia
    if (
      netoFinal <= 0 ||
      gerenteIds.includes(parseInt(opportunity.ejecutivo)) ||
      (opportunity.ejecutivo === "32" && fechaFactura >= EXECUTIVE_CUTOFF_DATE)
    )
      return 0;
    let percentage = 0;
    if (opportunity.pais === "Perú") {
      percentage = 1.25;
      if (month <= 5) percentage += 3.75;
      if (year === 2025 && month >= 2 && opportunity.ejecutivo === "306") percentage += 2.5;
      if (month >= 6) {
        percentage += 1;
        // +2.5% para intermediarios 45,23,43 (requiere contexto de oportunidad)
      }
    } else if (opportunity.pais === "Chile" && opportunity.ejecutivo) {
      if (year === 2025) {
        if (month <= 2) percentage = 8;
        else if (month <= 5) percentage = 7;
        else if (month <= 8) percentage = 6;
        else if (month <= 11) percentage = 5;
      } else if (year === 2026) {
        if (month <= 2) percentage = 4;
        else if (month <= 5) percentage = 3;
        else if (month <= 11) percentage = 2;
      }
    }
    return netoFinal * (percentage / 100);
  },
};
